import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, forwardRef, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageCropModel } from '../utilities/models/image-crop-model';
import { BuilderService } from '../utilities/services/builder.service';
import { GlobalFunctionService } from '@app/system/services/global-function.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PopupComponent } from './popup/popup.component';
import { LocaleService } from '@app/system/services/locale.service';
import { VideoUploadModel } from '../utilities/models/video-upload-model';
import { FunctionsService } from '../utilities/services/functions.service';
import { HandleBarPipe } from '@app/system/pipes/handle-bar/handle-bar.pipe';

@Component({
	selector: 'app-video-upload',
	templateUrl: './video-upload.component.html',
	styleUrls: ['./video-upload.component.scss'],
	providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VideoUploadComponent), multi: true }]
})
export class VideoUploadComponent implements OnInit, ControlValueAccessor {
	/**
	 * @desc Angular Input Decorator - Common Parameters
	 * @param object field - file upload field data.
	 * @param formControl control - file upload control.
	 */
	@Input() field: VideoUploadModel;
	@Input() control: UntypedFormControl;
	@Input() progress;
	@Input() multiType: boolean = false;
	@ViewChild('thumbnailUpload') thumbnailUpload: HTMLInputElement;
	@ViewChild('videoSrcElement') videoSrcElement: ElementRef;

	videoThumbnail: any;
	videoSrc: any;
	videoDuration: any = '';
	videoFile: any;
	inValidThumbnail: any = null;
	validator: any;
	videoLoader = false;
	coverImageLoader = false;
	cropper: ImageCropModel;
	cropperControl: UntypedFormControl;
	thumbanilCropped = false;
	helpText: any = {
		size: '',
		type: ''
	};
	state = true;

	/**
	 * @desc Angular Output Decorator - Common Parameters
	 * @param object helpLineEmit - help line json data.
	 */
	@Output() helpLineEmit = new EventEmitter();
	constructor(
		private host: ElementRef<HTMLInputElement>,
		public controlFunctions: FunctionsService,
		private sanitizer: DomSanitizer,
		private handleBarPipe: HandleBarPipe,
		private builderService: BuilderService,
		private gfService: GlobalFunctionService,
		public popup: MatDialog,
		public locale: LocaleService,
		private cdref: ChangeDetectorRef
	) { }

	ngOnInit() {
		let size = this.field.additionalMetaData.fileSize.toString().replace(/ /g, '').replace(/[^0-9\.]/g, '').trim();
		if (this.field.additionalMetaData.fileSize.toString().includes('MB')) {
			size += ' MB';
		} else if (this.field.additionalMetaData.fileSize.toString().includes('KB')) {
			size += ' KB';
		} else {
			size += ' GB';
		}
		this.helpText.size = size;
		this.helpText.type = this.field.additionalMetaData.supportedType.toString().split('.').join(' ').toUpperCase();
	}

	ngOnChanges(changes) {
		if (
			changes.hasOwnProperty('field') &&
			changes.field.currentValue && changes.field.currentValue.value &&
			typeof changes.field.currentValue.value === 'object'
		) {
			if (![null, undefined, ''].includes(changes.field.currentValue.value.thumbnail)) {
				this.videoThumbnail = changes.field.currentValue.value.thumbnail;
				this.state = false;
			} else {
				this.videoThumbnail = null;
				this.state = true;
			}
			if (![null, undefined, ''].includes(changes.field.currentValue.value.videoFile)) {
				if (typeof changes.field.currentValue.value.videoFile === 'string') {
					this.videoSrc = changes.field.currentValue.value.videoFile;
				} else {
					this.videoLoader = true;
					this.videoBuild(changes.field.currentValue.value.videoFile);
				}
			}
		}
	}

	triggerFile(input: HTMLInputElement) {
		if (navigator.onLine) {
			this.control.markAsTouched();
			input.click();
		}
	}

	resetFile() {
		this.field.value = null;
		this.control.patchValue(null, { emitEvent: false });
		this.videoThumbnail = null;
		this.videoFile = null;
		this.videoSrc = null;
		this.validator = this.control.validator;
		this.control.setValidators(this.validator);
		this.control.updateValueAndValidity({ emitEvent: false });
		this.control.setErrors({ required: this.field.isRequired });
		this.control.markAsTouched();
	}

	/**
	 * @desc Help line field change method and emit the data.
	 * @param {json} data help line json data.
	 */
	helpLineData(data) {
		this.helpLineEmit.emit(data);
	}

	uploadVideo(event, file: HTMLInputElement) {
		if (navigator.onLine) {
			this.state = true;
			if (event.target.files[0]) {
				this.videoLoader = true;
				const file = event.target.files[0];
				const validator = this.control.validator;
				this.control.clearValidators();
				this.control.patchValue(file.name, { emitEvent: false });
				this.control.patchValue(file, { emitModelToViewChange: false, emitEvent: false });
				this.control.setValidators(validator);
				this.control.updateValueAndValidity({ emitEvent: false });
				this.field.value = file;
				if (this.control.valid) {
					this.videoBuild(file);
				}
			}
			file.value = null;
		}
	}

	videoBuild(file) {
		const fileReader = new FileReader();
		fileReader.onload = () => {
			const blob = new Blob([fileReader.result], { type: file.type });
			const url = URL.createObjectURL(blob);
			this.videoSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
			this.cdref.detectChanges();
			this.videoSrcElement.nativeElement.preload = 'auto';
			this.videoSrcElement.nativeElement.addEventListener('loadeddata', this.videoLoaded.bind(this));
			this.videoLoader = false;
		};
		this.videoFile = file;
		fileReader.readAsArrayBuffer(file);
		this.control.setErrors({
			incorrect: {
				msg: this.locale.translate('video_thumbnail_upload')
			}
		});
		this.control.markAsUntouched();
	}

	videoLoaded(event) {
		this.videoDuration = this.formatTime(event.target.duration);
		this.setThumbnail(this.videoSrcElement.nativeElement);
	}

	formatTime(timeInSeconds) {
		// Hours, minutes and seconds
		let hrs: any = ~~(timeInSeconds / 3600);
		let mins: any = ~~((timeInSeconds % 3600) / 60);
		let secs: any = ~~timeInSeconds % 60;

		// Output like "1:01" or "4:03:59" or "123:03:59"
		let ret;

		if (hrs > 0) {
			hrs = (hrs < 10 ? ('0' + hrs) : hrs);
		} else {
			hrs = '00';
		}

		if (mins > 0) {
			mins = (mins < 10 ? ('0' + mins) : mins);
		} else {
			mins = '00';
		}

		if (secs > 0) {
			secs = (secs < 10 ? ('0' + secs) : secs)
		} else {
			secs = '00';
		}

		ret = `${hrs}:${mins}:${secs}`;
		return ret;
	}

	async setThumbnail(video) {
		const width = this.field.additionalMetaData.thumbnailWidth || 200;
		const height = this.field.additionalMetaData.thumbnailHeight || 100;

		const canvas = document.createElement('canvas');
		canvas.height = height;
		canvas.width = width;
		const ctx = canvas.getContext('2d');
		ctx.drawImage(video, 0, 0, width, height);
		const src = canvas.toDataURL("image/jpg");
		const file = await this.srcToFile(src, `thumbnail.jpg`, 'image/jpg');
		this.setVideoData(src, file);
	}

	srcToFile(src, fileName, mimeType) {
		return fetch(src)
			.then((res) => {
				return res.arrayBuffer();
			})
			.then((buf) => {
				return new File([buf], fileName, { type: mimeType });
			});
	}

	validateThumbnail(thumbnail) {
		const sizeValidator = (file, sizeVal) => {
			if (file.size > Number(sizeVal)) {
				// this.fileTypeError[formField.fieldColumn] = true;
				return {
					file: {
						message: this.handleBarPipe.transform(this.locale.translate('file_size'), {
							size: formatBytes(sizeVal)
						}),
						currentFileSize: file.size,
						actualFileSize: sizeVal
					}
				};
			}
			return null;
		};

		const formatBytes = (bytes: number) => {
			const si = true;
			const thresh = si ? 1000 : 1024;
			if (Math.abs(bytes) < thresh) {
				return bytes + ' BYTES';
			}
			const units = si
				? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
				: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
			let u = -1;
			do {
				bytes /= thresh;
				++u;
			} while (Math.abs(bytes) >= thresh && u < units.length - 1);
			return bytes.toFixed(1) + ' ' + units[u];
		};

		let returnData = null;

		const size = this.field.additionalMetaData.thumbnailMaxSize;
		if (![null, undefined].includes(size)) {
			if (typeof size === 'string' && (size.includes('MB') || size.includes('KB') || size.includes('BYTES'))) {
				const data = Number(size.replace(/ /g, '').replace(/[^0-9\.]/g, '').trim()) || 0;
				if (size.includes('MB')) {
					returnData = sizeValidator(thumbnail, data * 1e6);
				} else if (size.includes('KB')) {
					returnData = sizeValidator(thumbnail, data * 1000);
				} else if (size.includes('GB')) {
					// returnData = sizeValidator(control.value, data * 1e+9);
				} else {
					returnData = sizeValidator(thumbnail, data);
				}
			}
		}

		const thumbFormat = this.field.additionalMetaData.thumbnailFormat;
		if (![null, undefined].includes(thumbFormat)) {
			const fileExt = '.' + thumbnail.type.split('/')[1].toLowerCase();
			if (
				(typeof thumbFormat === 'string' && thumbFormat !== fileExt) ||
				(Array.isArray(thumbFormat) && thumbFormat.length > 0 && !thumbFormat.includes(fileExt))
			) {
				returnData = {
					file: {
						allowedExtension: thumbFormat,
						givenExtension: fileExt,
						message: this.locale.translate('invalid_file_format')
					}
				};
			}
		}

		return returnData;
	}

	pickThumbnail() {
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			height: 'auto',
			width: 'auto',
			maxWidth: 'auto',
			data: {
				action: 'videoThumbnailPop',
				screenId: 'videoThumbnailPop',
				popup: true,
				videoSrc: this.videoSrc,
				width: this.field.additionalMetaData.thumbnailWidth || 200,
				height: this.field.additionalMetaData.thumbnailHeight || 100
			},
			disableClose: true
		});
		dialogRef.afterClosed().subscribe(async (result) => {
			if (result) {
				this.setVideoData(result.src, result.file);
			}
		});
	}

	setVideoData(thumbnailSrc, thumbnailFile) {
		this.state = false;
		this.videoThumbnail = thumbnailSrc;
		this.thumbanilCropped = true;
		const file = {
			videoFile: this.videoFile,
			thumbnail: thumbnailFile,
			duration: this.videoDuration
		};
		if (this.field.additionalMetaData.dataUrl) {
			file.videoFile = this.videoSrc;
			file.thumbnail = thumbnailSrc;
		}

		const validator = this.control.validator;
		this.validator = this.control.validator;
		this.control.clearValidators();
		const emitEvent = (this.multiType) ? this.multiType : false;
		this.control.patchValue(file, { emitModelToViewChange: false, emitEvent });
		this.control.setValidators(validator);
		this.control.updateValueAndValidity({ emitEvent: false });
	}

	async uploadThumbnail($event, file: HTMLInputElement) {
		if (navigator.onLine) {
			this.thumbanilCropped = false;
			this.coverImageLoader = true;
			this.inValidThumbnail = this.validateThumbnail($event.target.files[0]);
			if ($event.target.files[0] && !this.inValidThumbnail) {
				const reader = new FileReader();
				reader.onload = (e) => {
					// this.videoThumbnail = e.target.result;
				};
				const src: any = await this.gfService.fileToDataURL($event);
				this.cropper = {
					fieldColumn: 'thumbnail',
					fieldType: 'imageCrop',
					additionalMetaData: {
						cropBoxResizable: false,
						dragMode: 'move',
						showBackground: true,
						dragType: 'rectangle',
						autoCrop: true,
						zoom: false,
						width: this.field.additionalMetaData.thumbnailWidth || 200,
						height: this.field.additionalMetaData.thumbnailHeight || 100,
						toggleDragModeOnDblclick: false,
						showLabel: false,
						dataUrl: this.field.additionalMetaData.dataUrl,
						showUploadIcon: false
					},
					isHidden: false,
					isReadonly: false,
					isRequired: true,
					value: null,
					fieldHelpText: this.locale.translate('use_mouse_scroll_to_zoom')
				};
				this.cropper.value = {
					imageSrc: src
				};
				this.builderService.fieldModelBuilder(this.cropper);
				this.cropperControl = this.builderService.controlBuilder(this.cropper);
				reader.readAsDataURL($event.target.files[0]);
				this.coverImageLoader = false;
			}
			file.value = null;
		}
	}

	removeThumbnail() {
		if (navigator.onLine) {
			this.thumbanilCropped = true;
			if (this.state) {
				this.control.setErrors({
					incorrect: {
						msg: this.locale.translate('video_thumbnail_upload')
					}
				});
				this.control.markAsTouched();
			}
		}
	}

	async submit() {
		if (navigator.onLine) {
			this.videoThumbnail = null;
			this.state = false;
			this.videoThumbnail = this.cropperControl.value.croppedFile;
			if (!this.field.additionalMetaData.dataUrl) {
				this.videoThumbnail = await this.gfService.fileToDataURL(this.cropperControl.value.croppedFile);
			}
			this.thumbanilCropped = true;
			const file = {
				videoFile: this.videoFile,
				thumbnail: this.cropperControl.value.croppedFile,
				duration: this.videoDuration
			};
			if (this.field.additionalMetaData.dataUrl) {
				file.videoFile = this.videoSrc;
			}

			const validator = this.control.validator;
			this.validator = this.control.validator;
			this.control.clearValidators();
			this.control.patchValue(file, { emitModelToViewChange: false, emitEvent: false });
			this.control.setValidators(validator);
			this.control.updateValueAndValidity();
		}
	}

	changeThumbnail() {
		if (navigator.onLine) {
			this.removeThumbnail();
		}
	}

	writeValue(value: null) { }

	registerOnChange(fn: () => void) { }

	registerOnTouched(fn: () => void) { }
}
